'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import PageBanner from './PageBanner';
import { EDITIONS, LANGS_COUNT, searchTranslations, getSurahMeta, getTranslation, getArabic } from '@/lib/quran-data';
import { Search, Bookmark, BookmarkX, RotateCcw, Languages, BarChart3, Scale, CircleDot } from 'lucide-react';
import { cn } from '@/lib/utils';

const DHIKR_PRESETS = [
  { ar: 'سُبْحَانَ الله', fr: 'SubhanAllah', audio: 'subhanallah' },
  { ar: 'الْحَمْدُ لِلَّه', fr: 'Alhamdulillah', audio: 'alhamdulillah' },
  { ar: 'اللهُ أَكْبَر', fr: 'Allahu Akbar', audio: 'allahuakbar' },
  { ar: 'لَا إِلَٰهَ إِلَّا الله', fr: 'La ilaha illa Allah', audio: 'lailaha' },
];

// Son de clic via Web Audio API (oscillateur, aucun fichier requis)
function playClickSound(frequency = 880) {
  try {
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = frequency;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.16);
    setTimeout(() => ctx.close(), 300);
  } catch { /* audio non disponible */ }
}

export default function ToolsSection() {
  const { uiLang, translationEd, setTranslationEd, bookmarks, toggleBookmark, readSet, listenedSet, resetProgress } = useApp();
  const t = DICTS[uiLang];
  const [query, setQuery] = useState('');
  const [tasbih, setTasbih] = useState(0);
  const [tasbihTarget, setTasbihTarget] = useState(33);
  const [dhikrIdx, setDhikrIdx] = useState(0);
  const lastCelebrate = useRef(0);

  const results = useMemo(() => (query.trim().length >= 2 ? searchTranslations(query, translationEd) : []), [query, translationEd]);
  const readCount = Object.keys(readSet).length;
  const listenedCount = Object.keys(listenedSet).length;

  return (
    <>
      <PageBanner
        src="/images/banners/tools_banner.png"
        eyebrow="Tools — الأدوات"
        title={t.tools.title}
        sub={t.tools.subtitle}
      />
      <section id="section-tools" className="mx-auto max-w-5xl scroll-mt-20 px-4 py-14">

      <div className="grid gap-5 md:grid-cols-2">
        {/* Search */}
        <div className="gold-ornate-card rounded-2xl p-5">
          <h3 className="mb-1 flex items-center gap-2 font-serif-royal text-lg font-bold text-[#f0d98c]">
            <Search className="h-4 w-4" /> {t.tools.search_title}
          </h3>
          <p className="mb-3 text-xs text-[#cfe0cf]/65">{t.tools.search_desc}</p>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.tools.search_placeholder}
            className="w-full rounded-md border border-[rgba(212,175,55,0.35)] bg-[#032019] px-4 py-2.5 text-sm text-[#f5f0e1] placeholder:text-[#cfe0cf]/35 outline-none focus:ring-2 focus:ring-[#d4af37]/50"
          />
          {query.trim().length >= 2 && (
            <div className="gold-scroll mt-3 max-h-56 space-y-2 overflow-y-auto">
              {results.length === 0 ? (
                <p className="py-3 text-center text-xs text-[#cfe0cf]/50">{t.tools.search_empty}</p>
              ) : (
                <>
                  <p className="text-[11px] font-semibold text-[#d4af37]">{results.length} {t.tools.results}</p>
                  {results.slice(0, 20).map((r) => (
                    <div key={`${r.surah}:${r.ayah}`} className="rounded-lg border border-[rgba(212,175,55,0.2)] bg-[rgba(3,32,25,0.6)] p-3">
                      <p className="mb-1 text-[10px] font-bold tracking-widest text-[#d4af37]/70">
                        {getSurahMeta(r.surah).nameEn} {r.surah}:{r.ayah}
                      </p>
                      <p className={cn('text-sm', r.arabic && 'text-[#cfe0cf]/90')}>{r.text}</p>
                      {r.arabic && <p className="font-arabic mt-1 text-right text-lg text-[#f5f0e1]/90" dir="rtl">{r.arabic}</p>}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* Bookmarks */}
        <div className="gold-ornate-card rounded-2xl p-5">
          <h3 className="mb-1 flex items-center gap-2 font-serif-royal text-lg font-bold text-[#f0d98c]">
            <Bookmark className="h-4 w-4" /> {t.tools.bookmarks_title}
          </h3>
          <p className="mb-3 text-xs text-[#cfe0cf]/65">{t.tools.bookmarks_desc}</p>
          {bookmarks.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-6 text-[#cfe0cf]/40">
              <BookmarkX className="h-8 w-8" />
              <p className="text-xs">{t.tools.no_bookmarks}</p>
            </div>
          ) : (
            <div className="gold-scroll max-h-56 space-y-2 overflow-y-auto">
              {bookmarks.map((b) => (
                <div key={`${b.surah}:${b.ayah}`} className="flex items-center justify-between gap-2 rounded-lg border border-[rgba(212,175,55,0.2)] bg-[rgba(3,32,25,0.6)] px-3 py-2">
                  <span className="text-xs font-semibold text-[#f0d98c]">
                    {getSurahMeta(b.surah).nameEn} · {b.surah}:{b.ayah}
                  </span>
                  <button
                    onClick={() => toggleBookmark(b.surah, b.ayah)}
                    className="rounded px-2 py-1 text-[10px] font-semibold text-[#ff9b9b]/80 hover:bg-[#e05252]/15"
                  >
                    {t.tools.remove_bookmark}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tasbih */}
        <div className="gold-ornate-card flex flex-col items-center rounded-2xl p-5 text-center">
          <h3 className="mb-1 flex items-center gap-2 font-serif-royal text-lg font-bold text-[#f0d98c]">
            <CircleDot className="h-4 w-4" /> {t.tools.tasbih_title}
          </h3>
          <p className="mb-3 text-xs text-[#cfe0cf]/65">{t.tools.tasbih_desc}</p>
          {/* Sélecteur de dhikr */}
          <div className="mb-3 flex flex-wrap justify-center gap-1.5">
            {DHIKR_PRESETS.map((d, i) => (
              <button
                key={d.audio}
                onClick={() => setDhikrIdx(i)}
                className={cn(
                  'rounded-full px-3 py-1 text-[11px] font-semibold transition-colors',
                  dhikrIdx === i ? 'bg-[#d4af37] text-[#032019]' : 'border border-[rgba(212,175,55,0.3)] text-[#cfe0cf]/75 hover:bg-[rgba(212,175,55,0.1)]'
                )}
              >
                <span className="font-arabic" dir="rtl">{d.ar}</span>
              </button>
            ))}
          </div>
          {/* Cibles 33 / 99 / 100 */}
          <div className="mb-3 flex gap-1.5">
            {[33, 99, 100].map((n) => (
              <button
                key={n}
                onClick={() => setTasbihTarget(n)}
                className={cn(
                  'rounded px-2.5 py-1 text-[10px] font-bold transition-colors',
                  tasbihTarget === n ? 'bg-[rgba(212,175,55,0.3)] text-[#f0d98c]' : 'text-[#cfe0cf]/50 hover:text-[#f0d98c]'
                )}
              >
                {n}
              </button>
            ))}
          </div>
          <button
            onClick={() => {
              const next = tasbih + 1;
              setTasbih(next);
              playClickSound(660 + (next % 33) * 8);
              if (navigator.vibrate) navigator.vibrate(next % tasbihTarget === 0 ? [80, 60, 120] : 35);
              if (next % tasbihTarget === 0 && Date.now() - lastCelebrate.current > 1500) {
                lastCelebrate.current = Date.now();
                playClickSound(1320);
                if (navigator.vibrate) navigator.vibrate([120, 80, 120, 80, 200]);
              }
            }}
            className="group relative flex h-36 w-36 items-center justify-center rounded-full border-2 border-[rgba(212,175,55,0.5)] bg-gradient-to-br from-[rgba(212,175,55,0.18)] to-transparent transition-transform active:scale-95"
            aria-label="tasbih counter"
          >
            <span className="font-serif-royal text-5xl font-bold text-[#f0d98c]">{tasbih}</span>
            <span className={cn(
              'absolute bottom-4 text-[10px] font-semibold tracking-widest transition-colors',
              tasbih > 0 && tasbih % tasbihTarget === 0 ? 'text-[#7ee2a8]' : 'text-[#cfe0cf]/40'
            )}>
              {t.tools.tasbih_target} : {tasbihTarget}
            </span>
            {tasbih > 0 && (
              <svg className="absolute inset-0 -rotate-90" viewBox="0 0 144 144">
                <circle
                  cx="72" cy="72" r="68" fill="none"
                  stroke="rgba(212,175,55,0.6)" strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 68}
                  strokeDashoffset={2 * Math.PI * 68 * (1 - (tasbih % tasbihTarget) / tasbihTarget)}
                />
              </svg>
            )}
          </button>
          <div className="mt-3 font-arabic text-lg text-[#f0d98c]" dir="rtl">{DHIKR_PRESETS[dhikrIdx].ar}</div>
          <div className="mt-1 flex gap-4 text-center text-[11px] text-[#cfe0cf]/55">
            <span>سبحان الله</span>
            <span>الحمد لله</span>
            <span>الله أكبر</span>
          </div>
          <button
            onClick={() => { setTasbih(0); playClickSound(440); }}
            className="mt-3 flex items-center gap-1.5 rounded-md border border-[rgba(212,175,55,0.35)] px-4 py-1.5 text-xs font-semibold text-[#f0d98c] hover:bg-[rgba(212,175,55,0.12)]"
          >
            <RotateCcw className="h-3 w-3" /> {t.tools.tasbih_reset}
          </button>
        </div>

        {/* Progress */}
        <div className="gold-ornate-card rounded-2xl p-5">
          <h3 className="mb-1 flex items-center gap-2 font-serif-royal text-lg font-bold text-[#f0d98c]">
            <BarChart3 className="h-4 w-4" /> {t.tools.progress_title}
          </h3>
          <p className="mb-4 text-xs text-[#cfe0cf]/65">{t.tools.progress_desc}</p>
          <div className="space-y-4">
            <div>
              <div className="mb-1 flex justify-between text-xs">
                <span className="text-[#cfe0cf]/80">{t.tools.read_count}</span>
                <span className="font-bold text-[#f0d98c]">{readCount} / 25</span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-[#032019]">
                <div className="h-full rounded-full bg-gradient-to-r from-[#9a7b1e] to-[#f0d98c] transition-all" style={{ width: `${Math.min(100, (readCount / 25) * 100)}%` }} />
              </div>
            </div>
            <div>
              <div className="mb-1 flex justify-between text-xs">
                <span className="text-[#cfe0cf]/80">{t.tools.listened_count}</span>
                <span className="font-bold text-[#f0d98c]">{listenedCount} / 25</span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-[#032019]">
                <div className="h-full rounded-full bg-gradient-to-r from-[#1a5c47] to-[#2e8b6e] transition-all" style={{ width: `${Math.min(100, (listenedCount / 25) * 100)}%` }} />
              </div>
            </div>
          </div>
          <button
            onClick={resetProgress}
            className="mt-4 flex items-center gap-1.5 rounded-md border border-[rgba(212,175,55,0.35)] px-4 py-1.5 text-xs font-semibold text-[#f0d98c] hover:bg-[rgba(212,175,55,0.12)]"
          >
            <RotateCcw className="h-3 w-3" /> {t.tools.reset_progress}
          </button>
        </div>
      </div>

      {/* Language comparison */}
      <div className="gold-ornate-card mt-5 rounded-2xl p-5">
        <h3 className="mb-1 flex items-center gap-2 font-serif-royal text-lg font-bold text-[#f0d98c]">
          <Scale className="h-4 w-4" /> {t.tools.compare_title}
        </h3>
        <p className="mb-3 text-xs text-[#cfe0cf]/65">{t.tools.compare_desc}</p>
        <CompareWidget />
      </div>

      {/* 24 languages grid */}
      <div className="gold-ornate-card mt-5 rounded-2xl p-5">
        <h3 className="mb-1 flex items-center gap-2 font-serif-royal text-lg font-bold text-[#f0d98c]">
          <Languages className="h-4 w-4" /> {t.tools.language_grid_title}
        </h3>
        <p className="mb-4 text-xs text-[#cfe0cf]/65">{t.tools.language_grid_desc}</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {EDITIONS.map((e) => (
            <button
              key={e.id}
              onClick={() => setTranslationEd(e.id)}
              className={cn(
                'flex items-center gap-2 rounded-lg border px-3 py-2.5 text-xs font-semibold transition-all',
                translationEd === e.id
                  ? 'border-[#d4af37] bg-[rgba(212,175,55,0.15)] text-[#f0d98c]'
                  : 'border-[rgba(212,175,55,0.2)] text-[#cfe0cf]/80 hover:border-[rgba(212,175,55,0.5)] hover:bg-[rgba(212,175,55,0.07)]'
              )}
            >
              <span className="text-base" aria-hidden>{e.flag}</span>
              <span className="truncate">{e.native}</span>
              {e.rtl && <span className="ml-auto text-[9px] tracking-wider text-[#d4af37]/60">RTL</span>}
            </button>
          ))}
          {/* Placeholder cells to the 1000-language goal */}
          <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-[rgba(212,175,55,0.2)] px-3 py-2.5 text-[10px] font-semibold tracking-wider text-[#cfe0cf]/35">
            + 979 LANGUES
          </div>
        </div>
      </div>
      </section>
    </>
  );
}

function CompareWidget() {
  const { surah, translationEd } = useApp();
  const [langA, setLangA] = useState(translationEd);
  const [langB, setLangB] = useState('en.sahih');
  const [ayahIdx, setAyahIdx] = useState(0);

  const arabic = getArabic(surah);
  const idx = Math.min(ayahIdx, arabic.length - 1);
  const a = getTranslation(surah, langA)[idx];
  const b = getTranslation(surah, langB)[idx];

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select value={langA} onChange={(e) => setLangA(e.target.value)} className="rounded-md border border-[rgba(212,175,55,0.35)] bg-[#032019] px-3 py-2 text-xs text-[#f5f0e1] outline-none">
          {EDITIONS.map((e) => <option key={e.id} value={e.id}>{e.flag} {e.native}</option>)}
        </select>
        <span className="text-[#d4af37]">⇄</span>
        <select value={langB} onChange={(e) => setLangB(e.target.value)} className="rounded-md border border-[rgba(212,175,55,0.35)] bg-[#032019] px-3 py-2 text-xs text-[#f5f0e1] outline-none">
          {EDITIONS.map((e) => <option key={e.id} value={e.id}>{e.flag} {e.native}</option>)}
        </select>
        <select value={idx} onChange={(e) => setAyahIdx(Number(e.target.value))} className="rounded-md border border-[rgba(212,175,55,0.35)] bg-[#032019] px-3 py-2 text-xs text-[#f5f0e1] outline-none">
          {arabic.map((ay, i) => <option key={ay.n} value={i}>Verset {ay.n}</option>)}
        </select>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-[rgba(212,175,55,0.25)] bg-[rgba(3,32,25,0.6)] p-4">
          {a && <p className="text-sm leading-relaxed text-[#e8dcc0]" dir={a.text.match(/[\u0600-\u06FF]/) ? 'rtl' : 'ltr'}>{a.text}</p>}
        </div>
        <div className="rounded-lg border border-[rgba(212,175,55,0.25)] bg-[rgba(3,32,25,0.6)] p-4">
          {b && <p className="text-sm leading-relaxed text-[#e8dcc0]" dir={b.text.match(/[\u0600-\u06FF]/) ? 'rtl' : 'ltr'}>{b.text}</p>}
        </div>
      </div>
    </div>
  );
}
